import {Grid, Switch, FormControlLabel, TextField} from "@mui/material";
import "dayjs/locale/it";
import dayjs from "dayjs";
import React, {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useFormContext} from "react-hook-form";
import {FormAutocomplete, FormDateTimePicker} from "../../common/form-fields";
import {SelectElementModelBE, SelectElementModelFE} from "../../api/types/generic";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {DeskDiscountGovernanceFormModel, DeskDiscountGovernanceGetData} from "../../api/types/deskDiscountGovernance";
import {useDeskDiscountGovernanceRepository} from "../../api/repositories/DeskDiscountGovernanceRepository";
import DealerAndTenant from "../../common/components/DealerAndTenant";

const DeskDiscountGovernanceForm = () => {

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {globalData: {user}} = useContext(GlobalContext);

    // Use Form Context
    const {register, setValue, watch, formState: {errors}} = useFormContext<DeskDiscountGovernanceFormModel>();

    const [responseData, setResponseData] = useState<DeskDiscountGovernanceGetData>();

    // Use Repository Hooks
    const {getDeskDiscountGovernanceDatas} = useDeskDiscountGovernanceRepository();

    // Legal Entity options
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const [locationModel, setLocationModel] = useState<SelectElementModelFE[]>([]);
    const [profilesModel, setProfilesModel] = useState<SelectElementModelFE[]>([]);

    const getResponseData = async (legalEntities: string[]) => {
        const responseData: DeskDiscountGovernanceGetData = await getDeskDiscountGovernanceDatas(legalEntities, []);
        const location = responseData?.locations.map(el => ({
            code: el.id,
            label: el.name
        })).sort((a, b) => a.label > b.label ? 1 : -1);
        setLocationModel([{code: 0, label: t("pricing:actions.selectAll")}, ...location]);
        const profiles = responseData?.profiles?.map(el => ({
            code: el.id,
            label: el.name
        })).sort((a, b) => a.label > b.label ? 1 : -1);
        setProfilesModel([{code: 0, label: t("pricing:actions.selectAll")}, ...profiles]);
        setResponseData(responseData);
    };

    useEffect(() => {
        const legalEntity = watch("legalEntity");
        if (legalEntity !== null && legalEntity !== undefined) {
            setLocationModel([]);
            setProfilesModel([]);
            setResponseData(undefined);
            setValue("prcLocation", []);
            setValue("lppProfiles", []);
            getResponseData(
                legalEntity.code ? [legalEntity.code as string] : everyLegalEntitiesWithoutAll
            );
        }
    }, [watch("legalEntity")]);

    const onChangeLocation = (input: SelectElementModelFE[]) => {
        const value: SelectElementModelFE[] =
            input.find((el: SelectElementModelFE) => el.code === 0) &&
            !watch().prcLocation?.find((el: SelectElementModelFE) => el.code === 0)
                ? locationModel.filter((el: SelectElementModelFE) => el.code !== 0)
                : input.filter((el) => el.code !== 0);
        setValue("prcLocation", value);
    };

    const onChangeProfile = (input: SelectElementModelFE[]) => {
        const value: SelectElementModelFE[] =
            input.find((el: SelectElementModelFE) => el.code === 0) &&
            !watch().lppProfiles?.find((el: SelectElementModelFE) => el.code === 0)
                ? profilesModel.filter((el: SelectElementModelFE) => el.code !== 0)
                : input.filter((el) => el.code !== 0);
        setValue("lppProfiles", value);
    };

    return (
        <Grid container spacing={2} alignItems="start">
            <Grid container item xs={12} md={12} lg={12} spacing={2} rowSpacing={3}>
                {/* LEGAL ENTITY & DEALER */}
                <DealerAndTenant
                    md={3}
                    legalEntityName={"legalEntity"}
                    dealerName={"dealer"}
                    dealerIsVisible={true}
                />
                {/* LOCATION */}
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        multiple={true}
                        {...register(`prcLocation`)}
                        label={`${t("pricing:deskDiscountGovernance.location")}*`}
                        options={locationModel || []}
                        isOptionEqualToValue={(
                            option: SelectElementModelFE,
                            value: SelectElementModelFE
                        ) => option.code === value.code}
                        handleChange={onChangeLocation}
                    />
                </Grid>
                {/* PROFILES */}
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        multiple={true}
                        {...register(`lppProfiles`)}
                        label={`${t("pricing:deskDiscountGovernance.profiles")}*`}
                        options={profilesModel || []}
                        isOptionEqualToValue={(
                            option: SelectElementModelFE,
                            value: SelectElementModelFE
                        ) => option.code === value.code}
                        handleChange={onChangeProfile}
                    />
                </Grid>
                {/* DISCOUNT MIN */}
                <Grid item xs={12} md={1.5}>
                    <TextField
                        {...register("discountMin")}
                        label={`${t("pricing:deskDiscountGovernance.minPercentage")}*`}
                        type="number"
                        size="small"
                        fullWidth
                        inputProps={{min: 0, max: 100, step: 5}}
                        error={!!errors.discountMin}
                        helperText={errors.discountMin?.message}
                    />
                </Grid>
                {/* DISCOUNT MAX */}
                <Grid item xs={12} md={1.5}>
                    <TextField
                        {...register("discountMax")}
                        label={`${t("pricing:deskDiscountGovernance.maxPercentage")}*`}
                        type="number"
                        size="small"
                        fullWidth
                        inputProps={{min: 0, max: 100, step: 5}}
                        error={!!errors.discountMax}
                        helperText={errors.discountMax?.message}
                    />
                </Grid>
                {/*
                  * VALIDITY FROM / TO
                  * F25 (LPP-21950) - T2.60b, punto 4: i due campi scrivono `validityDateFrom`/`To` ma
                  * erano etichettati "Prenotazione dal/al" con la chiave condivisa
                  * `ancillary.form.labels`. Ora usano le chiavi proprie della governance.
                  */}
                <Grid item xs={12} md={3}>
                    <FormDateTimePicker
                        {...register("validityDateFrom")}
                        label={`${t("pricing:deskDiscountGovernance.validityFrom")}*`}
                        minDate={dayjs()}
                    />
                </Grid>
                {/* VALIDITY TO */}
                <Grid item xs={12} md={3}>
                    <FormDateTimePicker
                        {...register("validityDateTo")}
                        label={`${t("pricing:deskDiscountGovernance.validityTo")}`}
                        minDate={watch("validityDateFrom") || dayjs()}
                    />
                </Grid>
                {/* ACTIVE TOGGLE */}
                <Grid item xs={12} md={3} display="flex" alignItems="center" sx={{pl: 4, pt: 2, mt: 2}}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={watch("active") ?? true}
                                onChange={(e) => setValue("active", e.target.checked)}
                                color="primary"
                                sx={{mr: 1}}
                            />
                        }
                        label={t("pricing:deskDiscountGovernance.enabled")}
                        sx={{ml: 1}}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
};

export default DeskDiscountGovernanceForm;
